// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Typography } from '@mui/material';
import { ReactComponent as LogoCitec } from '@shared/ui-kit/assets/img/logo-citec.svg';
import { ReactComponent as LogoNuvara } from '@shared/ui-kit/assets/img/logo-nuvara360.svg';
import DemoScreenshot from '@shared/ui-kit/assets/img/demo-screenshot.png';
import DemoScreenshotES from '@shared/ui-kit/assets/img/demo-screenshot-es.png';
import { LanguageSelector } from '@shared/ui-kit/components/language-selector';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useLanding } from './use-landing';

export const Landing = defineComponent({
  displayName: 'Landing',
  useSetup: useLanding,
  View: ({ onLogin, onSignUp, language, domain, t }) => (
    <div className="grid h-full animate-fade grid-cols-1 animate-duration-150 xl:grid-cols-[56%_auto]">
      <section className="hidden  flex-col bg-gradient-to-b from-[#010022] to-[#06335B] p-10 animate-duration-500 xl:flex">
        <div className="mb-24 ml-16 flex flex-col items-start gap-y-5">
          {domain === 'nuvara' ? (
            <LogoNuvara className="mb-24 h-16 w-[182px]" />
          ) : (
            <LogoCitec className="mb-24 h-16 w-[182px]" />
          )}
          <Typography
            variant="h1"
            className="!mb-5 animate-fade !text-5xl !font-semibold !text-white"
          >
            {t('auth.welcome.title', { ns: 'auth' })}
          </Typography>
          <Typography
            variant="h2"
            className="animate-fade !text-3xl !font-light !text-white"
          >
            {t('auth.welcome.subtitle', { ns: 'auth' })}
          </Typography>
        </div>
        {language === 'en' && (
          <img
            src={DemoScreenshot}
            alt="Demo Screenshot"
            className="animate-fade self-center"
            data-testid="demo-screenshot"
          />
        )}
        {language === 'es' && (
          <img
            src={DemoScreenshotES}
            alt="Demo Screenshot ES"
            className="animate-fade self-center"
            data-testid="demo-screenshot-es"
          />
        )}
      </section>

      <section className="flex flex-col p-4">
        <div className="self-end text-title-alt">
          <LanguageSelector />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center">
          <Typography
            variant="h1"
            className="!mb-10 !text-5xl !font-semibold !text-sidebar-bg-end"
          >
            {t('auth.welcome.starting-text', { ns: 'auth' })}
          </Typography>
          <div className="grid grid-cols-[225px] gap-x-16 gap-y-8 md:grid-cols-[repeat(2,minmax(0,225px))]">
            <Button
              onClick={onLogin}
              variant="outlined"
              color="secondary"
              className="!h-14 !rounded-3xl !text-2xl !font-normal"
              data-testid="login-button"
            >
              {t('auth.welcome.login-text-button', { ns: 'auth' })}
            </Button>
            <Button
              onClick={onSignUp}
              variant="contained"
              color="secondary"
              className="!h-14 !rounded-3xl !text-2xl !font-normal"
              data-testid="sign-up-button"
            >
              {t('auth.welcome.signup-text-button', { ns: 'auth' })}
            </Button>
          </div>
        </div>
      </section>
    </div>
  ),
});
